import React, { useState, useRef, useEffect } from 'react';
import { AspectRatio } from '@/components/ui/aspect-ratio';
import { Play } from 'lucide-react';

interface InlineShortPlayerProps {
  id: string;
  title: string;
  thumbnailUrl: string;
  onClick: () => void;
}

const InlineShortPlayer: React.FC<InlineShortPlayerProps> = ({ 
  id, 
  title, 
  thumbnailUrl, 
  onClick 
}) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [showThumbnail, setShowThumbnail] = useState(true);
  const [isReady, setIsReady] = useState(false);
  const iframeRef = useRef<HTMLIFrameElement>(null);

  useEffect(() => {
    // Preload the iframe after a short delay
    const timer = setTimeout(() => {
      setIsReady(true);
    }, 500);

    return () => clearTimeout(timer);
  }, []);

  const handlePlay = () => {
    setShowThumbnail(false);
    setIsPlaying(true);
  };

  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault();
    onClick();
  };

  return (
    <div 
      className="group relative cursor-pointer overflow-hidden rounded-lg shadow-sm focus-within:ring-2 focus-within:ring-primary"
      onClick={handleClick}
      role="button"
      tabIndex={0}
      aria-label={`Watch YouTube Short: ${title}`}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick();
        }
      }}
    >
      <AspectRatio ratio={9/16}>
        {showThumbnail && (
          <div className="relative h-full w-full">
            <img
              src={thumbnailUrl}
              alt={`${title} - YouTube Short from Everhouse`}
              loading="lazy"
              className="h-full w-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/0 via-black/20 to-black/50" />
            <div className="absolute bottom-2 left-2 right-2 flex items-center gap-2 text-white">
              <button 
                onClick={(e) => {
                  e.stopPropagation();
                  handlePlay();
                }}
                className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-black/60 hover:bg-black/80 transition-colors"
                aria-label="Play video"
              >
                <Play size={16} className="ml-0.5" />
              </button>
              <p className="line-clamp-1 text-sm">{title}</p>
            </div>
          </div>
        )}
        
        {isReady && (
          <iframe
            ref={iframeRef}
            src={`https://www.youtube.com/embed/${id}?autoplay=${isPlaying ? 1 : 0}&mute=1&loop=1&playlist=${id}&controls=0&modestbranding=1&rel=0&showinfo=0&iv_load_policy=3&disablekb=1&fs=0`}
            title={title}
            allow="autoplay; encrypted-media"
            allowFullScreen={false}
            className={`h-full w-full ${showThumbnail ? 'pointer-events-none opacity-0' : 'opacity-100'} transition-opacity duration-300`}
            style={{ border: 'none', outline: 'none' }}
          />
        )}
      </AspectRatio>
    </div>
  );
};

export default InlineShortPlayer;